import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, Globe } from "lucide-react";
import { selectPrimary, selectSubtext, subtextCycleCount } from "../config/stageMessages";
import { useUiMode } from "../hooks/useUiMode";
import ThinkingPane from "./ThinkingPane";
import ToolCallPill from "./ToolCallPill";
import ChartCard from "./ChartCard";
import ClarificationCard from "./ClarificationCard";
import ProgressNarration from "./ProgressNarration";
import FollowUpChips from "./FollowUpChips";
import AssumptionBanner from "./AssumptionBanner";

// Parse a follow-up list block (lines like "- question") into cleaned strings.
function parseFollowUpList(listBlock) {
  const suggestions = [];
  for (const rawLine of listBlock.split("\n")) {
    const line = rawLine.trim();
    if (!line) continue;
    const item = line.replace(/^([-*+]|\d+[.)])\s+/, "");
    if (item === line) continue; // not a list item — ignore stray prose
    const cleaned = item
      .replace(/^\*\*(.*)\*\*$/, "$1")
      .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1") // [text](url) → text
      .replace(/[`*_]/g, "")
      .trim();
    if (cleaned) suggestions.push(cleaned);
  }
  return suggestions.slice(0, 5);
}

// Split a trailing "Follow-up questions" markdown section off the body (B-UX-FOLLOWUP-1).
// Contract (Sprint 8a): the systemPrompt now pins the EXACT heading "## Follow-up questions",
// so we match that first for reliability. The looser variant regex is kept as a fallback so
// older conversations and minor LLM drift still parse. Returns { body, suggestions }; if no
// section is found, returns the text unchanged with an empty suggestions array.
function splitFollowUps(text) {
  if (!text) return { body: text || "", suggestions: [] };

  // 1. Pinned heading — exact "## Follow-up questions" on its own line.
  const pinnedRe = /\n[ \t]*## Follow-up questions[ \t]*\n([\s\S]*)$/;
  const pinned = text.match(pinnedRe);
  if (pinned) {
    const suggestions = parseFollowUpList(pinned[1]);
    if (suggestions.length > 0) return { body: text.slice(0, pinned.index).trimEnd(), suggestions };
  }

  // 2. Fallback — flexible variants (Follow-up / Follow up / Followup / Suggested follow-ups,
  //    as ## / ### / **bold**). Preserves Sprint 7's tolerant behavior.
  const re = /\n\s*(?:#{1,4}\s*|\*\*\s*)?(?:suggested\s+)?follow[\s-]?ups?(?:\s+questions?)?\s*:?\s*\*{0,2}\s*\n([\s\S]*)$/i;
  const m = text.match(re);
  if (!m) return { body: text, suggestions: [] };
  const suggestions = parseFollowUpList(m[1]);
  return { body: text.slice(0, m.index).trimEnd(), suggestions };
}

// Bug 5: roll up consecutive tool-call pills with the same tool name into ONE pill with a
// "×N" count. When the model fires the same tool many times (e.g. aggregate_answers_by_question
// ×11 in parallel) we'd otherwise render 11 identical pills. count===1 groups render exactly as
// before (full pill, incl. the SQL accordion for query_database); count>1 groups render a
// compact rolled-up pill with an aggregate status + summary. Only CONSECUTIVE same-name calls
// merge, so an interleaved sequence (A, B, A) stays as three pills.
function groupConsecutiveToolCalls(toolCalls) {
  const runs = [];
  for (const tc of toolCalls) {
    const last = runs[runs.length - 1];
    if (last && last.name === tc.name) last.items.push(tc);
    else runs.push({ name: tc.name, items: [tc] });
  }
  return runs.map((run) => {
    const items = run.items;
    const count = items.length;
    if (count === 1) return { key: items[0].tool_use_id, count: 1, toolCall: items[0] };
    const anyInFlight = items.some((t) => t.status === "decided" || t.status === "started");
    const completed = items.filter((t) => t.status === "completed").length;
    const failed = items.filter((t) => t.status === "failed").length;
    const status = anyInFlight ? "started" : (failed === count ? "failed" : "completed");
    const summary = anyInFlight
      ? `${completed}/${count} done`
      : failed > 0
        ? `${completed} ok, ${failed} failed`
        : `${count} calls completed`;
    return {
      key: items[0].tool_use_id,
      count,
      // Synthesized representative call; drop per-call SQL since multiple calls are merged.
      toolCall: { ...items[0], status, summary, error: null, executedSql: undefined, rowCount: undefined },
    };
  });
}

// Markdown link renderer that flags external (http/https) links with a small globe icon —
// the Sprint 7 inline-citation styling for web-search-derived content (B-UX-WEB-1/2). Used
// only for the AI Assistant engine so the legacy chart-agent rendering is untouched.
function CitationLink({ href, children, ...props }) {
  const isExternal = typeof href === "string" && /^https?:\/\//i.test(href);
  return (
    <a
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      className={isExternal ? "text-blue-700 hover:underline inline-flex items-baseline gap-1" : undefined}
      {...props}
    >
      {isExternal && <Globe className="w-3 h-3 text-blue-500 inline-block translate-y-0.5" strokeWidth={2} />}
      {children}
    </a>
  );
}

export default function AssistantMessage({ message, onClarificationSelect, onFollowUpSelect }) {
  const { uiMode } = useUiMode();
  const hasThinking = message.thinking && message.thinking.length > 0;
  const toolCalls = message.toolCalls || [];
  const progressItems = message.progressItems || [];
  const charts = message.charts || [];
  const hasAnyContent =
    hasThinking ||
    (message.visibleText && message.visibleText.length > 0) ||
    toolCalls.length > 0 ||
    progressItems.length > 0 ||
    charts.length > 0 ||
    !!message.clarification;
  // Pending indicator surfaces during two distinct waits:
  //   1. Pre-content: nothing has arrived yet (initial connect, first
  //      LLM round-trip).
  //   2. Between iterations: a tool just completed and the model is
  //      reasoning about the next step but hasn't started streaming.
  // Suppressed while a tool pill is itself spinning (status decided/started)
  // — that pill already conveys activity, no need to double up.
  const isInFlight = !message.durationMs;
  const hasInFlightTool = toolCalls.some(
    (tc) => tc.status === "decided" || tc.status === "started",
  );
  const showPending =
    isInFlight && !hasInFlightTool && (!hasAnyContent || !!message.currentStage);

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="space-y-3"
    >
      {(message.assumptions || []).map((a, i) => (
        <AssumptionBanner key={`assumption-${i}`} assumption={a} />
      ))}
      {hasThinking && <ThinkingPane text={message.thinking} isStreaming={!message.durationMs} />}
      {progressItems.length > 0 && <ProgressNarration items={progressItems} />}
      {groupConsecutiveToolCalls(toolCalls).map((group) => (
        <ToolCallPill key={group.key} toolCall={group.toolCall} count={group.count} />
      ))}
      {charts.map((spec, i) => (
        <ChartCard key={i} spec={spec} />
      ))}
      {charts.length > 0 && !message.durationMs && message.lastEventType === "chart" && (
        <ChartSkeleton />
      )}
      {message.clarification && (
        <ClarificationCard
          {...message.clarification}
          onSelect={onClarificationSelect}
        />
      )}
      {message.visibleText && (() => {
        // AI Assistant engine (signalled by an onFollowUpSelect handler): once the turn is
        // done, split off the follow-up section so the user sees chips instead of a raw
        // markdown list, and render external links with the citation-globe styling. The
        // legacy chart-agent path (no onFollowUpSelect) renders exactly as before.
        const isAssistant = typeof onFollowUpSelect === "function";
        const done = !!message.durationMs;
        const { body, suggestions } =
          isAssistant && done ? splitFollowUps(message.visibleText) : { body: message.visibleText, suggestions: [] };
        const mdComponents = isAssistant ? { a: CitationLink } : undefined;
        return (
          <>
            <div className="text-[15px] leading-relaxed text-gray-900 prose prose-sm max-w-none prose-p:my-2 prose-strong:text-gray-900 prose-strong:font-semibold prose-code:text-pink-700 prose-code:bg-pink-50 prose-code:px-1 prose-code:rounded">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                {body}
              </ReactMarkdown>
            </div>
            {isAssistant && suggestions.length > 0 && (
              <FollowUpChips suggestions={suggestions} onSelect={onFollowUpSelect} />
            )}
          </>
        );
      })()}
      {showPending && <PendingIndicator stage={message.currentStage} />}
      {message.durationMs && uiMode === "dev" && (
        <div className="text-xs text-gray-400 mt-3">
          {(message.durationMs / 1000).toFixed(1)}s
          {message.tokens && ` · ${message.tokens.input + message.tokens.output} tokens`}
        </div>
      )}
    </motion.div>
  );
}

// Chart-shaped placeholder shown between consecutive chart renders during
// the same assistant turn. Only renders when the most recent event was an
// add_chart, the assistant is still in flight, and we're in the lull
// before the next tool call decision lands. Hides as soon as the next
// event arrives (lastEventType flips) or the turn finishes (durationMs).
function ChartSkeleton() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="bg-white border border-gray-200 rounded-xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
    >
      <div className="space-y-3 animate-pulse">
        <div className="h-4 bg-gray-100 rounded w-1/3"></div>
        <div className="h-32 bg-gray-50 rounded"></div>
      </div>
      <div className="mt-3 text-xs text-gray-400 text-center">Preparing next chart…</div>
    </motion.div>
  );
}

function PendingIndicator({ stage }) {
  // Primary is drawn once per stage transition (random pick from the
  // stage's primaryPool). Subtext is drawn fresh at each cycle advance
  // — same pool, but a new random entry — so the user sees varied copy
  // even when the same stage fires across multiple iterations.
  const [primary, setPrimary] = useState(() => selectPrimary(stage));
  const [cycleIdx, setCycleIdx] = useState(-1);
  const [subtext, setSubtext] = useState(null);

  useEffect(() => {
    setPrimary(selectPrimary(stage));
    setCycleIdx(-1);
    setSubtext(null);

    const cycleCount = subtextCycleCount(stage);
    if (cycleCount === 0) return;

    let intervalId = null;
    let nextIdx = 0;
    const startTimer = setTimeout(() => {
      setCycleIdx(0);
      setSubtext(selectSubtext(stage, 0));
      nextIdx = 1;
      intervalId = setInterval(() => {
        const idx = Math.min(nextIdx, cycleCount - 1);
        setCycleIdx(idx);
        setSubtext(selectSubtext(stage, idx));
        if (idx >= cycleCount - 1) clearInterval(intervalId);
        nextIdx = idx + 1;
      }, 3500);
    }, 3000);
    return () => {
      clearTimeout(startTimer);
      if (intervalId !== null) clearInterval(intervalId);
    };
  }, [stage]);

  const hasSub = cycleIdx >= 0 && subtext !== null;

  return (
    <div className={`flex gap-2.5 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg ${hasSub ? "items-start" : "items-center"}`}>
      <Loader2
        className={`w-4 h-4 text-purple-500 animate-spin flex-shrink-0${hasSub ? " mt-0.5" : ""}`}
        strokeWidth={2}
      />
      <div>
        <div className="text-sm font-medium text-gray-600">
          {primary}<DotsAnimation />
        </div>
        {hasSub && (
          <motion.div
            key={cycleIdx}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, ease: "easeIn" }}
            className="text-xs text-gray-400 mt-1"
          >
            {subtext}
          </motion.div>
        )}
      </div>
    </div>
  );
}

function DotsAnimation() {
  // Static "…" character renders as the fallback when framer-motion fails
  // to hydrate; otherwise it pulses gently.
  return (
    <motion.span
      animate={{ opacity: [0.2, 1, 1, 0.2] }}
      transition={{
        duration: 1.4,
        repeat: Infinity,
        ease: "easeInOut",
        times: [0, 0.3, 0.6, 1],
      }}
    >
      …
    </motion.span>
  );
}
